import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { StaffTemplateForm } from '@go-client/components';
import { capitalize } from '@go-shared/lib/string';
import {
  bare,
  create,
  update,
  exportStaffTemplatesToCSV,
  paginateStaffTemplates,
} from '@go-client/lib/api/staffTemplate';
import { StaffTemplate, BareStaffTemplate } from '@go-shared/types/staffTemplate';

import './StaffLibrary.scss';
import { Popup } from '@spark-labs/avocado-ui';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  BubbleBadge,
  BubbleButton,
  BubbleDebounce,
  BubbleInput,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import { PartialBy } from '@go-shared/types/typeUtils';
import Libraries from './Libraries';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canManageLibraries } from '@go-shared/lib/permissions';
import { LibrariesSubNavigation } from '@go-client/components/Navigations/LibrariesSubNavigation';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import { hashStringToColor } from '@go-shared/lib/utils';
import { EditedStaffTemplate } from '@go-client/components/StaffTemplateForm';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';

interface StaffLibraryDataBundle {
  staffTemplates: StaffTableItem[];
}

type StaffTableItem = {
  id: string;
  title: string;
  department: string;
  berthing: string;
  quantity: number;
  _source: StaffTemplate;
};

interface PaginationParams<T> {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  search?: string;
}

type StaffLibraryPageProps = RouteComponentProps;

export default function StaffLibrary(props: StaffLibraryPageProps) {
  const { ...rest } = props;
  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();

  const [loading, setLoading] = useState(true);

  const [dataBundle, setDataBundle] = useState<StaffLibraryDataBundle | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [showStaffTemplateForm, setShowStaffTemplateForm] = useState(false);
  const [staffTemplateFormMode, setStaffTemplateFormMode] = useState<'create' | 'edit' | undefined>(
    undefined
  );
  const [selectedStaffTemplate, setSelectedStaffTemplate] = useState<
    EditedStaffTemplate | undefined
  >(undefined);

  const [paginationParams, setPaginationParams] = useState<PaginationParams<BareStaffTemplate>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: '_id',
    sortDirection: SortOrder.DESC,
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  async function refresh() {
    setLoading(true);
    await fetchDataBundle();
  }

  useEffect(() => {
    refresh();
  }, [paginationParams]);

  async function fetchDataBundle() {
    setLoading(true);
    try {
      const { data, meta } = await paginateStaffTemplates({
        page: paginationParams.page,
        perPage: paginationParams.perPage,
        sortBy: paginationParams.sortBy,
        sortDirection: paginationParams.sortDirection,
        propertyId: property._id,
        ...(paginationParams.search && { search: paginationParams.search }),
      });

      setPaginationMetadata(meta);

      const staffTableItem = data.map(st => {
        return {
          id: st._id,
          title: st.title,
          department: st.department.name,
          berthing: st.berthing.name,
          quantity: st.quantity,
          _source: st,
        };
      });

      setDataBundle({
        staffTemplates:
          paginationParams.page > 1
            ? [...(dataBundle?.staffTemplates ?? []), ...staffTableItem]
            : staffTableItem,
      });
    } finally {
      setLoading(false);
    }
  }

  const showNewStaffBtn = canManageLibraries(user);
  let selected = selectedStaffTemplate;
  let popupAction = staffTemplateFormMode ? capitalize(staffTemplateFormMode) + ' Staff' : '';
  let staffTemplateTitle = selected ? selected.title : '';
  let popupTitle = `${popupAction}  ${
    staffTemplateFormMode === 'edit' ? '- ' + staffTemplateTitle : ''
  }`;

  let berthingLabel = property
    ? getPropertyConfiguration(property.type, 'berthingLabel')
    : 'Berthing';

  const tableFields: Array<BubbleTableFields<StaffTableItem>> = [
    {
      key: 'title',
    },
    {
      key: 'department',
    },
    {
      key: 'berthing',
      label: berthingLabel,
    },
    {
      key: 'quantity',
      label: 'Forecasted Qty.',
    },
    {
      key: 'actions',
      label: '',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'whitespace-nowrap p-1-i pr-4-i text-right' },
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<StaffTableItem>> = [
    {
      key: 'title',
      render: item => {
        return (
          <>
            <div>{item.title}</div>
          </>
        );
      },
    },
    {
      key: 'department',
      render: item => (
        <>
          <BubbleBadge
            key={item._source.department._id}
            color={hashStringToColor(item.department)}
            className="mr-1"
          >
            {item.department}
          </BubbleBadge>
        </>
      ),
    },
    {
      key: 'berthing',
      render: item => (
        <>
          <BubbleBadge
            key={item._source.berthing._id}
            color={hashStringToColor(item.berthing)}
            className="mr-1"
          >
            {item.berthing}
          </BubbleBadge>
        </>
      ),
    },
    {
      key: 'quantity',
      render: item => {
        let element = <></>;

        if (item.quantity >= 0) {
          element = <div>{item.quantity}</div>;
        }

        return element;
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            icon="edit"
            onClick={() => openUpdateStaffTemplateForm(item._source)}
          />
        </>
      ),
    },
  ];

  function headerFn() {
    return <LibrariesSubNavigation active="staff" />;
  }

  function bodyFn() {
    return (
      <>
        <BubbleTable
          responsive
          striped
          headVariant="titanium"
          fields={tableFields}
          items={dataBundle?.staffTemplates}
          keyExtractor={item => item._source._id}
          stickyHeader={182}
          templates={tableTemplates}
          showPretitle
          pretitleClassName="p-0-i"
          showAllItems={true}
          onRowDblClick={item => openUpdateStaffTemplateForm(item._source)}
          pretitleRender={() => (
            <div className="px-4 pt-4 pb-1">
              <BubbleDebounce
                onTrigger={[
                  (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                  500,
                ]}
              >
                {({ debounce }) => (
                  <BubbleInput
                    type="text"
                    variant="titanium"
                    block
                    value={paginationParams.search}
                    inputClassName="outline-none-i"
                    onValue={debounce}
                    placeholder="Search..."
                  />
                )}
              </BubbleDebounce>
            </div>
          )}
        />
        {paginationMetadata && (
          <BubbleTablePaginationFooter
            paginationMetadata={paginationMetadata}
            perPage={paginationParams.perPage}
            onLoadMoreClick={() =>
              setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
            }
            onPerPageChange={value =>
              setPaginationParams({ ...paginationParams, page: 1, perPage: value })
            }
          />
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {showNewStaffBtn && (
              <BubbleButton
                onClick={handleAddStaffClick}
                color="green"
                variant="lime"
                className="text-left"
                icon="sparkles"
              >
                Create Staff
              </BubbleButton>
            )}
            <BubbleButton
              variant="slate"
              onClick={generateCSV}
              className="text-left"
              icon="file-export"
            >
              Export to CSV
            </BubbleButton>
          </div>
        </div>
      </>
    );
  }

  function outerFn() {
    return (
      <>
        {showStaffTemplateForm && (
          <Popup title={popupTitle} onClose={handleCloseForm}>
            {selected && (
              <StaffTemplateForm
                propertyType={property.type}
                staffTemplate={selected}
                showNewStaffBtn={showNewStaffBtn}
                onSubmit={handleFormSubmit}
              />
            )}
          </Popup>
        )}
      </>
    );
  }

  function closeStaffTemplateForm() {
    setShowStaffTemplateForm(false);
  }

  function openCreateStaffTemplateForm() {
    let newStaffTemplate: EditedStaffTemplate = {
      _id: '',
      title: '',
      quantity: 0,
      property: property,
    };

    setShowStaffTemplateForm(true);
    setSelectedStaffTemplate(newStaffTemplate);
    setStaffTemplateFormMode('create');
  }

  function openUpdateStaffTemplateForm(staffTemplate: StaffTemplate) {
    setShowStaffTemplateForm(true);
    setSelectedStaffTemplate(staffTemplate);
    setStaffTemplateFormMode('edit');
  }

  async function handleFormSubmit(staffTemplate: StaffTemplate) {
    let bareStaffTemplate = bare(staffTemplate);
    if (staffTemplateFormMode === 'edit') {
      await update(bareStaffTemplate);
    } else if (staffTemplateFormMode === 'create') {
      let newStaffTemplate: PartialBy<BareStaffTemplate, '_id'> = { ...bareStaffTemplate };
      delete newStaffTemplate._id;
      await create(bareStaffTemplate);
    }

    closeStaffTemplateForm();
    refresh();
  }

  function handleCloseForm() {
    closeStaffTemplateForm();
  }

  function handleAddStaffClick() {
    openCreateStaffTemplateForm();
  }

  async function generateCSV() {
    const result = await exportStaffTemplatesToCSV(property._id);
    if (result.location) {
      window.open(result.location, '_blank');
    }
  }

  return (
    <Libraries
      active="libraries"
      user={user}
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...rest}
    />
  );
}
